import { useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import { Play, Timer } from "lucide-react";
import { format, parseISO } from "date-fns";
import { SiteHeader } from "@/components/SiteHeader";
import { SiteFooter } from "@/components/SiteFooter";
import { publicApi, videoUrl } from "@/lib/api";
import { ShareBar } from "@/components/ShareBar";
import { useStreak, StreakNote } from "@/components/DevotionalStreak";
import { useDevotionalsOn } from "@/lib/display";

const shareUrl = (id) => `${process.env.REACT_APP_BACKEND_URL}/api/share/devotionals/${id}`;

const ArchiveCard = ({ d, active, onClick }) => (
  <button onClick={onClick} data-testid={`devotional-card-${d.id}`}
    className={`group w-full rounded-2xl border p-5 text-left transition-[border-color,background-color] ${active ? "border-[#1D64B4] bg-[#EBF3FA]" : "border-slate-200 bg-white hover:border-[#1D64B4]"}`}>
    <div className="flex items-start gap-4">
      <span className="grid h-10 w-10 shrink-0 place-items-center rounded-full bg-[#0A3A6B] text-white transition-transform group-hover:scale-110"><Play className="h-4 w-4" /></span>
      <div>
        <p className="text-xs font-semibold uppercase tracking-wider text-slate-400">{format(parseISO(d.devotional_date), "MMM d, yyyy")}</p>
        <h3 className="mt-1 font-display font-semibold text-slate-900">{d.title}</h3>
        <p className="text-sm text-slate-500">{d.speaker}</p>
      </div>
    </div>
  </button>
);

export default function Devotionals() {
  const [items, setItems] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [current, setCurrent] = useState(null);
  const [speaker, setSpeaker] = useState("all");
  const [playedToday, setPlayedToday] = useState(false);
  const { streak, isMember, onWatch } = useStreak();
  const on = useDevotionalsOn();
  const isToday = current?.devotional_date === format(new Date(), "yyyy-MM-dd");
  const played = () => { if (isToday && !playedToday) { setPlayedToday(true); onWatch(); } };

  useEffect(() => {
    const want = new URLSearchParams(window.location.search).get("d");
    publicApi.get("/devotionals").then((r) => { setItems(r.data); setCurrent(r.data.find((d) => d.id === want) || r.data[0] || null); }).finally(() => setLoaded(true));
  }, []);

  const speakers = useMemo(() => [...new Set(items.map((d) => d.speaker))], [items]);
  const list = speaker === "all" ? items : items.filter((d) => d.speaker === speaker);

  return (
    <div className="min-h-screen bg-[#F8FAFC]" data-testid="devotionals-page">
      <SiteHeader />
      <main className="mx-auto max-w-7xl px-5 pb-24 pt-32 lg:px-10">
        <p className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-[#1D64B4]"><Timer className="h-4 w-4" /> Daily 2-minute devotionals</p>
        <h1 className="mt-3 text-4xl font-bold tracking-tight text-slate-900 sm:text-5xl lg:text-6xl">Two minutes with God.</h1>
        {loaded && items.length === 0 && (
          <p className="mt-10 rounded-3xl border border-dashed border-slate-300 bg-white p-10 text-slate-500" data-testid="devotionals-empty">
            {on ? "Our first devotional is on its way. Check back soon!" : "Our 2-minute devotionals are taking a short break. Please check back soon!"}
          </p>
        )}
        {current && (
          <div className="mt-12 grid gap-8 lg:grid-cols-[1.6fr_1fr]">
            <motion.div key={current.id} initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }}>
              <video key={current.id} src={videoUrl(current.id)} controls autoPlay={false} preload="metadata"
                onPlay={played} className="mx-auto aspect-[9/16] max-h-[80vh] w-full max-w-sm rounded-3xl bg-black object-cover shadow-xl" data-testid="devotional-player" />
              {isToday && playedToday && isMember && <div className="mx-auto mt-4 max-w-sm"><StreakNote streak={streak} /></div>}
              <h2 className="mt-6 text-2xl font-bold tracking-tight text-slate-900" data-testid="devotional-current-title">{current.title}</h2>
              <p className="mt-1 text-sm text-slate-500">{current.speaker} · {format(parseISO(current.devotional_date), "MMMM d, yyyy")}</p>
              <div className="mt-4"><ShareBar title={current.title} url={shareUrl(current.id)} testPrefix="devotional-share" /></div>
              {current.scripture && <p className="font-serif-accent mt-5 text-xl text-[#0A3A6B]">{current.scripture}</p>}
              {current.reflection && <p className="mt-4 whitespace-pre-line leading-relaxed text-slate-600">{current.reflection}</p>}
            </motion.div>
            <aside>
              <div className="mb-4 flex flex-wrap gap-2">
                {["all", ...speakers].map((s) => (
                  <button key={s} onClick={() => setSpeaker(s)} data-testid={`speaker-filter-${s}`}
                    className={`rounded-full px-4 py-1.5 text-sm transition-colors ${speaker === s ? "bg-[#0A3A6B] text-white" : "bg-white text-slate-600 hover:bg-slate-200"}`}>
                    {s === "all" ? "All speakers" : s}
                  </button>
                ))}
              </div>
              <div className="max-h-[640px] space-y-3 overflow-y-auto pr-1">
                {list.map((d) => <ArchiveCard key={d.id} d={d} active={d.id === current.id} onClick={() => setCurrent(d)} />)}
              </div>
            </aside>
          </div>
        )}
      </main>
      <SiteFooter />
    </div>
  );
}
